import type { MilitaryHearingLossFormState } from './useMilitaryHearingLossForm'
import { toInternationalNumber } from '../../../../../data/dialCodes'

// ─── Payload types ────────────────────────────────────────────────────────────

/** Service record as sent to the API — the client-only `id` field is stripped. */
export interface ServiceRecordPayload {
    serviceNumber: string
    branch: string
    enlistmentDate: string
    dischargeDate: string | null
}

/**
 * Shape of the object posted to the military hearing loss submit-lead endpoint.
 *
 * Extends the form state, omitting the two fields that are transformed:
 *   - `address3` → replaced by `city`
 *   - `services`  → replaced by `ServiceRecordPayload[]` (id stripped)
 *
 * All other form fields pass through unchanged, including `matrixOptInConfirmed`
 * (the agent's Matrix journey opt-in confirmation, required `true` by the backend).
 */
export interface MilitaryHearingLossPayload
    extends Omit<MilitaryHearingLossFormState, 'address3' | 'services' | 'telephoneCountry'> {
    /** address3 from the form, sent as city to satisfy the base schema. */
    city: string
    /** Service records with the client-only id stripped. */
    services: ServiceRecordPayload[]
    /** Derived from services[0].serviceNumber. */
    serviceNumber: string
    /** true when at least one service record is present. */
    servedUkArmedForces: boolean
    /** true when any service record has an enlistment year >= 1987. */
    servedAfter1987: boolean
    /** true when any service record is marked as still serving. */
    activeService: boolean
    /**
     * Year the claimant last left service — the most recent discharge year
     * across all records, or null when still serving.
     */
    leftServiceYear: number | null
    /** Confirm the correct value with the backend before going live. */
    introducer: string
    /** Name or email of the dashboard agent who submitted the lead. */
    added_by: string
}

// ─── Constants ───────────────────────────────────────────────────────────────

// Placeholder introducer value — confirm the correct string with the backend
// before going live, as this may need to change.
export const MILITARY_HEARING_LOSS_INTRODUCER = 'MilitaryHearingLoss'

export const NI_REGEX = /^[A-CEGHJ-PR-TW-Z]{2}[0-9]{6}[A-D]$/i
export const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
export const TEL_REGEX = /^[0-9+() -]+$/
// Mirrors the backend `ukPostcodeRegex` so a malformed postcode is rejected in
// the UI rather than only failing server-side.
export const POSTCODE_REGEX = /^(GIR ?0AA|[A-Z]{1,2}\d[A-Z\d]? ?\d[ABD-HJLNP-UW-Z]{2})$/i
// Mirrors the backend `personNameRegex`: any Latin letter incl. accents (Müller, José),
// plus spaces, apostrophes, hyphens and full stops
export const NAME_REGEX = /^\p{Script=Latin}[\p{Script=Latin}\p{M} '’.-]*$/u
// Non-UK postcodes are free text, matching the backend limit
export const INTERNATIONAL_POSTCODE_MAX = 20
// UK mobiles are 10+ digits; some countries' numbers are shorter (e.g. Iceland, 7), and Data8 does the real check
export const mobileMinLength = (telephoneCountry: string) => (telephoneCountry === 'GB' ? 9 : 7)
// Mirrors the backend `serviceNumberRegex` so the derived top-level serviceNumber
// passes the base schema. Covers:
//   British Army: 6, 7 or 8 digits
//   Royal Navy pre-JPA: 1 letter (D/W/C/N/P/V) + 6 digits + 1 letter
//   Royal Navy post-JPA / Royal Marines: 8 digits starting with 3
export const SERVICE_NUMBER_REGEX = /^(?:\d{6}|\d{7}|\d{8}|[DWCNPV][0-9]{6}[A-Z]|3[0-9]{7})$/i

// ─── Validation ──────────────────────────────────────────────────────────────

// Pure validation of the whole form. Returns the first error message found, or
// null when the form is submittable.
export const validateForm = (form: MilitaryHearingLossFormState): string | null => {
    if (!form.title.trim()) return 'Title is required'
    if (!form.firstname.trim()) return 'First name is required'
    if (form.firstname.trim().length < 2) return 'First name must be at least 2 characters'
    if (!NAME_REGEX.test(form.firstname.trim())) return 'First name can only contain letters, spaces, apostrophes and hyphens'
    if (form.middleNames.trim() && !NAME_REGEX.test(form.middleNames.trim())) return 'Middle names can only contain letters, spaces, apostrophes and hyphens'
    if (!form.lastname.trim()) return 'Surname is required'
    if (form.lastname.trim().length < 2) return 'Surname must be at least 2 characters'
    if (!NAME_REGEX.test(form.lastname.trim())) return 'Surname can only contain letters, spaces, apostrophes and hyphens'
    if (!form.dob.trim()) return 'Date of birth is required'
    if (!form.nationalInsurance.trim()) return 'National Insurance number is required'
    if (!NI_REGEX.test(form.nationalInsurance.replace(/\s/g, ''))) return 'Please enter a valid National Insurance number (e.g. AB123456C)'
    if (!form.address1.trim()) return 'Address line 1 is required'
    if (!form.address3.trim()) return 'City is required'
    if (!form.country) return 'Country is required'
    if (form.country === 'GB') {
        if (!form.postcode.trim()) return 'Postcode is required'
        if (!POSTCODE_REGEX.test(form.postcode.trim())) return 'Please enter a valid UK postcode'
    } else if (form.postcode.trim().length > INTERNATIONAL_POSTCODE_MAX) {
        return `Postcode must be ${INTERNATIONAL_POSTCODE_MAX} characters or less`
    }
    if (!form.telephone.trim()) return 'Mobile number is required'
    if (!TEL_REGEX.test(form.telephone.trim())) return 'Mobile number contains invalid characters'
    if (form.telephone.trim().length < mobileMinLength(form.telephoneCountry)) return 'Mobile number is too short'
    if (form.telephone.trim().length > 20) return 'Mobile number is too long'
    if (!form.email.trim()) return 'Email address is required'
    if (!EMAIL_REGEX.test(form.email.trim())) return 'Please enter a valid email address'
    for (let i = 0; i < form.services.length; i++) {
        const s = form.services[i]
        const n = i + 1
        if (!s.serviceNumber.trim()) return `Service ${n}: service number is required`
        if (!SERVICE_NUMBER_REGEX.test(s.serviceNumber.trim())) return `Service ${n}: please enter a valid service number`
        if (!s.branch) return `Service ${n}: branch is required`
        if (!s.enlistmentDate.trim()) return `Service ${n}: enlistment date is required`
        // Discharge date is only required when the claimant is no longer serving
        // in this role. If still serving, the field is left blank.
        if (!s.activeService && !s.dischargeDate.trim()) return `Service ${n}: discharge date is required`
        if (s.enlistmentDate && s.dischargeDate && s.dischargeDate < s.enlistmentDate) return `Service ${n}: discharge date must be on or after enlistment date`
    }
    if (!form.audiology) return 'Audiology is required'
    if (form.hearingLossOrTinnitus === null) return 'Tinnitus is required'
    if (form.hyperacusis === null) return 'Hyperacusis is required'
    if (!form.alternativeNoiseExposure) return 'Alternative noise exposure is required'
    if (form.alternateCivilianExposure === null) return 'Alternate civilian exposure is required'
    if (form.successfulAfcsWarPensionClaim === null) return 'AFCS / War Pension claim answer is required'
    if (form.successfulAfcsWarPensionClaim === true && form.afcsAwardAmount === null) return 'Award amount is required when AFCS / War Pension claim is yes'
    if (form.successfulAfcsWarPensionClaim === true && form.afcsAwardAmount !== null && form.afcsAwardAmount <= 0) return 'Award amount must be greater than £0'
    if (form.nfciClaim === null) return 'NFCI claim answer is required'
    if (form.specialForces === null) return 'Special Forces answer is required'
    if (!form.matrixOptInConfirmed) return 'Please confirm the client has agreed to enrol in the Matrix journey'
    return null
}

// Joi dumps `"services[0].dischargeDate" must be one of [null, ]` when a
// leftover date is sent for a still-serving role. Map those to agent copy.
export const friendlySubmitError = (raw: unknown): string => {
    const message = typeof raw === 'string' ? raw.trim() : ''
    if (!message) return 'Could not submit lead. Please check the form and try again.'

    const quoted = message.match(/^"([^"]+)"\s+(.+)$/)
    if (!quoted) return message

    const path = quoted[1]
    const rest = quoted[2]
    const serviceMatch = path.match(/^services\[(\d+)\]\.(.+)$/)
    const prefix = serviceMatch ? `Service ${Number(serviceMatch[1]) + 1}: ` : ''
    const field = serviceMatch ? serviceMatch[2] : path

    if (field === 'dischargeDate' && /must be one of/i.test(rest)) {
        return `${prefix}Leave the discharge date blank if the client is still serving`
    }
    if (/must be one of|fails to match|is not allowed|is required|must be a/i.test(rest)) {
        const label = field.replace(/([A-Z])/g, ' $1').replace(/\./g, ' ').toLowerCase().trim()
        return `${prefix}Please check the ${label}`
    }
    return message
}

// ─── Payload serialisation ────────────────────────────────────────────────────

// Recursively trims whitespace from every string value, including those nested
// inside arrays/objects (e.g. each service record). Non-string values pass
// through untouched.
const deepTrim = <T>(value: T): T => {
    if (typeof value === 'string') return value.trim() as T
    if (Array.isArray(value)) return value.map((v) => deepTrim(v)) as T
    if (value && typeof value === 'object') {
        return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, deepTrim(v)])) as T
    }
    return value
}

// Serialises the form state into the API payload. All string fields are trimmed.
// Boolean fields are stored as booleans in state and pass through unchanged.
export const buildPayload = (form: MilitaryHearingLossFormState, agentLabel: string): MilitaryHearingLossPayload => {
    const { services, address3, telephone, telephoneCountry, ...rest } = deepTrim(form)
    // hasActiveService is true if any record is marked as currently serving.
    const hasActiveService = services.some((s) => s.activeService)
    // leftServiceYear is the most recent discharge year across all records.
    // When still serving the backend expects null instead.
    const lastDischargeDate = services.map((s) => s.dischargeDate).filter(Boolean).sort().pop()
    const leftServiceYear = hasActiveService || !lastDischargeDate ? null : parseInt(lastDischargeDate.slice(0, 4))
    return {
        ...rest,
        // address3 sent as city to satisfy the base schema required field.
        city: address3,
        // Mobile is sent with its dial code, e.g. "+44 7700 900123" / "+49 151 23456789"
        telephone: toInternationalNumber(telephoneCountry, telephone),
        // Top-level fields required by the base schema — derived from services[].
        serviceNumber: services[0]?.serviceNumber?.trim() ?? '',
        servedUkArmedForces: services.length > 0,
        servedAfter1987: services.some((s) => parseInt(s.enlistmentDate.slice(0, 4)) >= 1987),
        activeService: hasActiveService,
        leftServiceYear,
        // Drop the client-only `id` and per-record `activeService` flag.
        // Still-serving roles must send a null discharge date — a leftover
        // value is rejected by the API as `"dischargeDate" must be one of [null, ]`.
        services: services.map(({ id, activeService: stillServing, ...service }) => ({
            ...service,
            dischargeDate: stillServing || !service.dischargeDate ? null : service.dischargeDate,
        })),
        // See MILITARY_HEARING_LOSS_INTRODUCER — confirm value with backend.
        introducer: MILITARY_HEARING_LOSS_INTRODUCER,
        // The name/email of the dashboard agent who submitted this lead.
        added_by: agentLabel.trim(),
    }
}
